import s from './MineExploreCardView.module.sass';
import React, { FC, useLayoutEffect, useState } from 'react';
import { Button, Form, FormItem, Header, Placeholder, ScrollView } from '$uikit';
import { useDispatch, useSelector } from 'react-redux';
import { mineActions, selectMine } from '$store/mine';
import { MineExploreCard, MineExploreCardContentTypeEnum } from '$store/models';
import { LoadingStatus } from '$store/reels/interface';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { mainActions } from '$store/main';
import { useTranslator, useUserInfo } from '$hooks';
import { Loader, UserCell } from '$shared/components';
import { IcCopy28, IcPlay28 } from '$assets';
import { batchActions } from '$store';
import copyToClipboard from 'copy-text-to-clipboard';
import { prettifyNumber } from '$utils/number';
import { _confirm } from '$utils';
import { MINE } from '$shared/constants/pages';
import { useRouter } from 'react-router5';
import { MineExploreNoFunds } from '$core/MineExplore/MineExploreNoFunds/MineExploreNoFunds';

export const MineExploreCardView: FC<{ cardId: number }> = ({ cardId }) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { explore } = useSelector(selectMine);
  const [card, setCard] = useState<MineExploreCard | null>(
    explore.filter((card) => card.id === cardId)[0] || null,
  );
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('loading');
  const owner = useUserInfo(card ? card.ownerId : 0);
  const router = useRouter();

  const loadData = () => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/mine/explore/${cardId}`)
      .then((resp) => {
        dispatch(
          batchActions(
            mainActions.setUser(resp.owner),
            mineActions.updateExploreCard(resp.card),
          ),
        );
        setCard(resp.card);
        setLoadingStatus('');
      })
      .catch((e) => {
        setLoadingStatus('failed');
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  useLayoutEffect(() => {
    loadData();
  }, [cardId]);

  const handleShare = () => {
    copyToClipboard(location.href);
    dispatch(toastActions.setToastSuccess(t('link_copied')));
  };

  const handleBuy = () => {
    dispatch(toastActions.setToastLoading());
    Api.post<any, any>(`/mine/explore/${cardId}/buy`)
      .then((resp) => {
        dispatch(mineActions.updateExploreCard(resp.card));
        dispatch(toastActions.hideToast());
        setCard(resp.card);

        const el = document.querySelector(`._mine_explore_cards_${card!.level}`);
        if (el) {
          el.scrollTo({
            left: 0,
            behavior: 'smooth',
          });
        }
      })
      .catch((e) => {
        if (e.code === 'insufficient_funds') {
          dispatch(toastActions.hideToast());
          dispatch(mainActions.setPopup(<MineExploreNoFunds />));
        } else {
          dispatch(toastActions.setToastFail(e.message));
        }
      });
  };

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={loadData}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if ((loadingStatus === 'loading' && !card) || !card) {
      return (
        <Placeholder>
          <Loader />
        </Placeholder>
      );
    }

    return (
      <ScrollView>
        <div className={s.contentWrap}>
          <div className={s.content}>
            {card.isBought ? (
              <>
                {card.contentType === MineExploreCardContentTypeEnum.video ? (
                  <video
                    controls
                    preload="auto"
                    disablePictureInPicture
                    playsInline
                    src={card.contentPath}
                    className={s.contentImage}
                  />
                ) : (
                  <img src={card.contentImageUrl} className={s.contentImage} />
                )}
              </>
            ) : (
              <div className={s.contentBlur}>
                {card.contentType === MineExploreCardContentTypeEnum.video && (
                  <div className="ExternalVideo__play_btn">
                    <IcPlay28 />
                  </div>
                )}
                <img src={card.thumb} className={s.contentBlurImage} />
              </div>
            )}
          </div>
        </div>
        <Form>
          <FormItem skipPadding>{owner && <UserCell objectId={owner.id} />}</FormItem>
          <FormItem>
            {card.isBought ? (
              <Button icon={<IcCopy28 />} onClick={handleShare}>
                {t('mine_explore_view_card_share')}
              </Button>
            ) : (
              <Button icon={<IcCopy28 />} onClick={handleBuy} mode="pay">
                {t('mine_explore_view_card_unlock', {
                  price: `${prettifyNumber(card.price)} PLACE`,
                })}
              </Button>
            )}
          </FormItem>
        </Form>
      </ScrollView>
    );
  }

  return (
    <>
      <Header showCloseButton>{t('mine_explore_view_card_title')}</Header>
      {renderContent()}
    </>
  );
};
